import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export const CheckEmail = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const email = location.state?.email || '';
  const requiresPayment = location.state?.requiresPayment || false;
  const [resending, setResending] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const [resendError, setResendError] = useState('');

  const handleResendEmail = async () => {
    if (!email) {
      setResendError('Email address not found. Please register again.');
      return;
    }

    setResending(true);
    setResendMessage('');
    setResendError('');

    try {
      const response = await fetch('/auth/resend-verification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      const data = await response.json();

      if (response.ok) {
        setResendMessage('Verification email sent! Please check your inbox.');
      } else {
        setResendError(data.message || 'Failed to resend email');
      }
    } catch (err) {
      setResendError('Network error. Please try again.');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-secondary-100 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center mb-6">
          <img src="/WAGo_logo.svg" alt="WAGo Logo" className="h-16" />
        </div>
        <div className="bg-white py-10 px-6 shadow-xl rounded-lg sm:px-10">
          <div className="text-center">
            <div className="mb-6">
              <div className="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-primary-100">
                <svg className="h-12 w-12 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
            </div>
            
            <h2 className="text-3xl font-extrabold text-gray-900 mb-4">
              Check your email
            </h2>
            
            {email && (
              <p className="text-sm text-gray-600 mb-6">
                We've sent a verification email to<br />
                <strong className="text-gray-900">{email}</strong>
              </p>
            )}
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-blue-800 mb-2">
                <strong>Next steps:</strong>
              </p>
              <ol className="text-left text-sm text-blue-700 space-y-2 ml-4">
                <li className="flex items-start">
                  <span className="font-semibold mr-2">1.</span>
                  <span>Check your inbox (and spam folder)</span>
                </li>
                <li className="flex items-start">
                  <span className="font-semibold mr-2">2.</span>
                  <span>Click the verification link in the email</span>
                </li>
                {requiresPayment ? (
                  <li className="flex items-start">
                    <span className="font-semibold mr-2">3.</span>
                    <span>Complete your subscription to activate your account</span>
                  </li>
                ) : (
                  <li className="flex items-start">
                    <span className="font-semibold mr-2">3.</span>
                    <span>Return here and sign in</span>
                  </li>
                )}
              </ol>
            </div>

            {requiresPayment && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-center mb-2">
                  <span className="text-2xl mr-2">💳</span>
                  <span className="font-semibold text-green-800">Subscription Required</span>
                </div>
                <p className="text-sm text-green-700">
                  After verifying your email, you'll be asked to subscribe to your first subaccount to start using GOWa.
                </p>
              </div>
            )}

            <div className="space-y-3">
              <button
                onClick={() => navigate('/login')}
                className="w-full flex justify-center py-2.5 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
              >
                Go to Login
              </button>
              
              {resendMessage && (
                <div className="rounded-md bg-green-50 p-3">
                  <p className="text-sm text-green-800">{resendMessage}</p>
                </div>
              )}
              
              {resendError && (
                <div className="rounded-md bg-red-50 p-3">
                  <p className="text-sm text-red-800">{resendError}</p>
                </div>
              )}
              
              <button
                onClick={handleResendEmail}
                disabled={resending || !email}
                className="w-full flex justify-center py-2.5 px-4 border border-primary-300 rounded-md shadow-sm text-sm font-medium text-primary-700 bg-white hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {resending ? 'Sending...' : 'Resend Verification Email'}
              </button>
              
              <p className="text-xs text-gray-500 mt-4">
                Didn't receive the email? Check your spam folder or use the button above to resend.
              </p>
            </div>
          </div>
        </div>
        
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600">
            Need help?{' '}
            <a href="mailto:info@gowa.app" className="font-medium text-primary-600 hover:text-primary-500">
              Contact Support
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default CheckEmail;
